// Cade-internal admin tenant — signup approval queue.
// Spec: docs/spec/core/part2-product-requirements.md §13.3 (Reqs 615-620).
// Spec: docs/spec/core/part3-ux-spec.md §9.10.
// Spec: docs/spec/topics/onboarding/approval-queue.md.
//
// Two-pane layout per mockup §X.10. Left pane is a filterable list of
// SignupRequest rows; right pane is the detail of the selected row with
// approve / reject / resend actions.
//
// Route gated on `currentUser.tenant.hasPlatformAdmin === true` — non-Cade-
// internal callers see <Navigate to="/" replace />. The auth service's
// admin.signupRequests.* procedures additionally return NOT_FOUND for
// non-Cade-internal callers, but the route gate is the user-facing UX
// layer.

import { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAnimatedNavigate } from '../../hooks/useAnimatedNavigate';
import { trpc } from '../../api/trpc';
import { NotFoundPage } from '../NotFound';
import { SignupRequestRow } from '../../components/admin/SignupRequestRow';
import { SignupRequestDetail } from '../../components/admin/SignupRequestDetail';

type StatusFilter = 'pending' | 'approved' | 'rejected' | 'all';

export function SignupRequestsPage() {
  // All hooks must run unconditionally on every render — React enforces a
  // stable hook order. Gating fetches goes through `enabled`, not by wrapping
  // the hook call in `if`/early-return.
  const navigate = useAnimatedNavigate();
  const { token, tenantId } = useAuth();
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  // Pre-select the request named in `?request=<id>` — the deep link the
  // "View request" button in the admin-signup-notification email uses.
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(
    () => searchParams.get('request'),
  );

  const hasSession = !!token && !!tenantId;

  // Route guard, layer 2: tenant.hasPlatformAdmin must be true. Per
  // Part 4 §14.10: "Visible only when currentUser.tenant.hasPlatformAdmin
  // === true. Other tenants see <NotFound /> if they navigate directly."
  // The auth service's admin.signupRequests procedures additionally
  // enforce hasPlatformAdmin-or-NOT_FOUND, but that would render an
  // empty page rather than 404. This gate gives the correct UX.
  const meQuery = trpc.auth.me.useQuery(undefined, { enabled: hasSession });

  const listQuery = trpc.auth.admin.signupRequests.list.useQuery(
    { sessionToken: token ?? '', status: statusFilter },
    { enabled: hasSession && meQuery.data?.hasPlatformAdmin === true },
  );

  // Route guard, layer 1: must have a session token.
  if (!token || !tenantId) {
    return <Navigate to="/login" replace />;
  }

  if (meQuery.isLoading) {
    return (
      <div style={{ padding: 32, color: 'var(--color-text-muted, #888)' }}>Loading…</div>
    );
  }
  if (meQuery.error || !meQuery.data?.hasPlatformAdmin) {
    return <NotFoundPage />;
  }

  return (
    <div className="settings-layout">
      <nav className="settings-sidebar">
        <div className="settings-sidebar-header">
          <button
            className="settings-sidebar-back"
            onClick={() => navigate('/')}
            title="Back to chat"
          >
            &larr;
          </button>
          <h2 className="settings-sidebar-title">Admin · Signup requests</h2>
        </div>

        <div className="settings-sidebar-section">
          <div className="settings-sidebar-section-title">Status</div>
          {(['pending', 'approved', 'rejected', 'all'] as const).map((s) => (
            <button
              key={s}
              className={`settings-sidebar-tab ${statusFilter === s ? 'is-active' : ''}`}
              onClick={() => {
                setStatusFilter(s);
                setSelectedRequestId(null);
              }}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </nav>

      <main className="settings-main" style={{ display: 'flex', gap: 0 }}>
        {/* List pane */}
        <section
          style={{
            flex: '0 0 380px',
            borderRight: '1px solid var(--color-border, #2a2a2a)',
            overflowY: 'auto',
            padding: '8px 0',
          }}
        >
          {listQuery.isLoading && (
            <div style={{ padding: 16, color: 'var(--color-text-muted)' }}>Loading…</div>
          )}
          {listQuery.error && (
            <div style={{ padding: 16, color: 'var(--color-text-error, #c33)' }}>
              {listQuery.error.message}
            </div>
          )}
          {listQuery.data && listQuery.data.length === 0 && (
            <div style={{ padding: 16, color: 'var(--color-text-muted)' }}>
              No {statusFilter === 'all' ? '' : statusFilter} requests.
            </div>
          )}
          {listQuery.data &&
            listQuery.data.map((sr: {
              id: string;
              email: string;
              emailDomain: string;
              name: string | null;
              company: string | null;
              role: string | null;
              status: string;
              captchaPassed: boolean;
              createdAt: Date | string;
              approvedAt: Date | string | null;
              rejectedAt: Date | string | null;
              createdTenantId: string | null;
            }) => (
              <SignupRequestRow
                key={sr.id}
                request={sr}
                isSelected={selectedRequestId === sr.id}
                onSelect={() => setSelectedRequestId(sr.id)}
              />
            ))}
        </section>

        {/* Detail pane */}
        <section style={{ flex: 1, padding: 24, overflowY: 'auto' }}>
          {selectedRequestId ? (
            <SignupRequestDetail
              requestId={selectedRequestId}
              sessionToken={token}
              onActionComplete={() => {
                listQuery.refetch();
                setSelectedRequestId(null);
              }}
            />
          ) : (
            <div style={{ color: 'var(--color-text-muted)', textAlign: 'center', marginTop: 80 }}>
              Select a request to review.
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
